/** @jsx jsx */

import { css, jsx } from '@emotion/core';

import CenteredSpinner from 'app/developer-v3/components/CenteredSpinner';
import ServiceUtils from 'app/service/ServiceUtils';
import { findAuthById } from 'app/auth/Selectors';
import { useQueryServiceImplementation } from 'app/developer-v3/hooks';

import Selector from './Selector';

import type { AuthType } from 'app/auth/types';
import type { Service } from 'app/service/types';

import type { FlowNode } from './types';

const Styles = {
  root: css`
    padding: 0 0 20px;

    &:last-of-type {
      padding-bottom: 0;
    }
  `,
};

export type Props = {
  auths: Array<AuthType>;
  connectAuth: (
    attrs: {},
    options: { onDangerouslyReceiveSuccess: (authId: number) => void }
  ) => void;
  node: FlowNode;
  onSaveAuth: (nodeAttrs: {}) => void;
  reconnectAuth: (
    id: number,
    params: { service: Service },
    onDangerouslyReceiveSuccess: (authId: number) => void
  ) => void;
  saveAuth: (id: number) => void;
  testAuth: (id: number) => void;
};

const Auth = (props: Props) => {
  const {
    auths,
    connectAuth,
    node,
    onSaveAuth,
    reconnectAuth,
    saveAuth,
    testAuth,
  } = props;

  const {
    data: serviceImplementation,
    isLoading,
  } = useQueryServiceImplementation({
    selectedApi: node.selected_api,
  });
  const service = {
    ...serviceImplementation,
    title: serviceImplementation?.name,
    id: serviceImplementation?.selectedApi,
  };

  const onChangeAuth = authentication_id => {
    const auth: AuthType = findAuthById(auths, authentication_id);
    if (auth && auth.canEdit) {
      saveAuth(authentication_id);
    }
    onSaveAuth({ authentication_id });
  };

  const onSave = ({ authentication_id }) => {
    // check authentication_id to make flow happy:
    if (authentication_id) {
      onChangeAuth(authentication_id);
    }
  };

  const connect = (attrs, { onDangerouslyReceiveSuccess }) => {
    connectAuth(attrs, {
      onDangerouslyReceiveSuccess: authentication_id => {
        // Need to call onChangeAuth with the new list of auths, as the props
        // haven't updated yet
        onChangeAuth(authentication_id);
        onDangerouslyReceiveSuccess(authentication_id);
      },
    });
  };

  return isLoading ? (
    <CenteredSpinner />
  ) : (
    <section css={Styles.root}>
      <Selector
        accountHeading={`${ServiceUtils.name(service)} account:`}
        auths={auths}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- intentionally focus the account picker only when no account is selected yet
        autoFocus={!node?.authentication_id}
        connectAuth={connect}
        isAutoSelectionEnabled={true}
        node={node}
        onSave={onSave}
        reconnectAuth={reconnectAuth}
        service={service}
        testAuth={testAuth}
      />
    </section>
  );
};

export default Auth;
